import Button from '@gaia/components/Button';
import {useToggle} from '@gaia/hooks/useToggle';
import theme from '@gaia/theme';
import type {ImportLoading, ImportState} from '@gaia/types';
import {idle as idleState} from '@gaia/types/ImportState';
import {getShortFilename} from '@gaia/utils/formatters';
import {filterNonNullable} from '@gaia/utils/typing';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    FormControl,
    FormControlLabel,
    FormGroup,
    Link,
    Typography,
} from '@mui/material';
import type {Feature, LineString, Point, Polygon} from 'geojson';

interface ConfirmImportDialogProps {
    importState: ImportLoading;
    setImportState: SetState<ImportState>;
}

interface ImportTypeLabelProps {
    label: string;
    items: number;
}

export function ConfirmImportDialog({importState, setImportState}: ConfirmImportDialogProps) {
    const [waypointsSelected, toggleWaypointsSelected] = useToggle(false);
    const [tracksSelected, toggleTracksSelected] = useToggle(false);
    const [areasSelected, toggleAreasSelected] = useToggle(false);

    const features = importState.pendingImport.features as Feature[];

    const waypoints = features.filter((f): f is Feature<Point> => f.geometry.type === 'Point');

    const tracks = features.filter(
        (f): f is Feature<LineString> => f.geometry.type === 'LineString'
    );

    const areas = features.filter((f): f is Feature<Polygon> => f.geometry.type === 'Polygon');

    return (
        <Dialog
            disableEscapeKeyDown
            open={importState.largeImportConfirmed === false}
            onClose={() => {
                // disable closing the dialog via escape key or backdrop click
                return false;
            }}
        >
            <DialogContent style={{width: 330}}>
                <Typography
                    variant='h5'
                    style={{marginBottom: theme.spacing(3), fontWeight: 'bold'}}
                >
                    Large file import
                </Typography>
                <Typography variant='body1' style={{marginBottom: theme.spacing(3)}}>
                    {getErrorMessage(
                        importState.fileNames,
                        importState.pendingImport.features.length
                    )}
                </Typography>

                <Typography variant='body1' style={{marginBottom: theme.spacing(3)}}>
                    {`Importing this file shouldn't be a problem, but it may take a while to display
                on the map before saving.`}
                </Typography>

                <Typography variant='body1' style={{marginBottom: theme.spacing(2)}}>
                    {`We recommend you confirm the item types you want to display on the map before
                importing to help speed things up:`}
                </Typography>

                <FormControl
                    variant='standard'
                    component='fieldset'
                    style={{
                        marginBottom: theme.spacing(2),
                        marginLeft: theme.spacing(1),
                    }}
                >
                    <FormGroup>
                        {waypoints.length > 0 && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        disableRipple
                                        checked={waypointsSelected}
                                        onChange={toggleWaypointsSelected}
                                        name='waypoints'
                                    />
                                }
                                label={
                                    <ImportTypeLabel label='Waypoints' items={waypoints.length} />
                                }
                            />
                        )}
                        {tracks.length > 0 && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        disableRipple
                                        checked={tracksSelected}
                                        onChange={toggleTracksSelected}
                                        name='tracks'
                                    />
                                }
                                label={<ImportTypeLabel label='Tracks' items={tracks.length} />}
                            />
                        )}
                        {areas.length > 0 && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        disableRipple
                                        checked={areasSelected}
                                        onChange={toggleAreasSelected}
                                        name='areas'
                                    />
                                }
                                label={<ImportTypeLabel label='Areas' items={areas.length} />}
                            />
                        )}
                    </FormGroup>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Link
                    style={{
                        marginLeft: theme.spacing(2),
                        display: 'flex',
                        flex: 1,
                    }}
                    href='https://help.gaiagps.com/hc/en-us/articles/115003525347-Import-GPX-KML-or-FIT-Files-on-gaiagps-com'
                    rel='noopener noreferrer'
                    target='_blank'
                >
                    <HelpOutlineIcon style={{fontSize: 14, marginRight: theme.spacing(0.5)}} />
                    File import help
                </Link>

                <Button appearance='secondary' onClick={() => setImportState(idleState)}>
                    Cancel
                </Button>
                <Button
                    appearance='primary'
                    onClick={() => {
                        const importTypes = [
                            waypointsSelected ? ('waypoints' as const) : undefined,
                            tracksSelected ? ('tracks' as const) : undefined,
                            areasSelected ? ('areas' as const) : undefined,
                        ].filter(filterNonNullable);

                        setImportState({
                            ...importState,
                            largeImportConfirmed: true,
                            importTypes,
                        });
                    }}
                    disabled={!waypointsSelected && !tracksSelected && !areasSelected}
                >
                    Import
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function ImportTypeLabel({label, items}: ImportTypeLabelProps) {
    return (
        <Typography variant='body1' style={{fontWeight: 500}}>
            {label} ({items.toLocaleString()} items)
        </Typography>
    );
}

function getErrorMessage(fileNames: string[], features: number) {
    if (fileNames.length === 1) {
        return (
            <>
                Your file <b>{getShortFilename(fileNames[0])}</b> contains{' '}
                <b>{features.toLocaleString()}</b> items.
            </>
        );
    } else {
        return (
            <>
                Your files contain <b>{features.toLocaleString()}</b> items.
            </>
        );
    }
}
